import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { EMPTY, Observable, Subject } from 'rxjs';
import { catchError, first, takeUntil, tap } from 'rxjs/operators';

import { AuthState } from '../../auth/application/auth.state';
import { SessionsService } from '../../infrastructure/sessions.service';

import { AppContext } from '../app.context';

@Injectable({
  providedIn: 'root',
})
export class ShopSessionService implements OnDestroy {
  private readonly serverSessionKey = 'plys-session-id';
  private readonly frontendSessionKey = 'plys-frontend-session-id';
  private sessionId: string | null = null;
  private initialized = false;
  private readonly destroy$ = new Subject<void>();
  private readonly _sessionClaimed = new Subject<void>();

  get sessionClaimed$(): Observable<void> {
    return this._sessionClaimed.asObservable();
  }

  constructor(
    private readonly sessionsService: SessionsService,
    private readonly authState: AuthState,
    private readonly router: Router,
    private readonly appContext: AppContext
  ) {}

  init(): void {
    if (this.initialized) return;
    this.initialized = true;

    if (!localStorage.getItem(this.frontendSessionKey)) {
      localStorage.setItem(this.frontendSessionKey, crypto.randomUUID());
    }

    this.sessionId = localStorage.getItem(this.serverSessionKey);

    this.authState.loginEvent$.pipe(takeUntil(this.destroy$)).subscribe(() => this.claimSession());
  }

  ensureSession(shopId: string): void {
    if (this.sessionId) return;

    this.createSession(shopId).subscribe();
  }

  getSessionId(): string | null {
    return this.sessionId;
  }

  private claimSession(retryOnNotFound = true): void {
    if (!this.sessionId) return;

    // Scope the claim to the cart the buyer is actively completing (the cart_id already in the URL),
    // so a shop session reused across accounts does not pull an earlier flow's / another account's
    // reservation onto this account (PAUL-2733). No cart in context -> whole-session claim, as before.
    const cartId = this.router.parseUrl(this.router.url).queryParams['cart_id'] ?? null;

    this.sessionsService
      .claimSession(this.sessionId, cartId)
      .pipe(
        first(),
        catchError((err: HttpErrorResponse) => {
          // The stored session id may no longer exist on the server (e.g. database restored
          // from another environment, PAUL-2772). Drop it, create a fresh session and retry
          // the claim once.
          if (err.error?.error?.type === 'not_found') {
            this.clearSession();
            if (retryOnNotFound) {
              this.recreateSessionAndRetryClaim();
            }
          }
          return EMPTY;
        })
      )
      .subscribe(() => this._sessionClaimed.next());
  }

  private recreateSessionAndRetryClaim(): void {
    // AppContext holds the shop the buyer is in right now; the localStorage copy is only a fallback
    // as it can still hold a previously visited shop's id (`get()` is used instead of the `shopId`
    // getter because the getter throws when unset).
    const shopId = this.appContext.get().shopId ?? localStorage.getItem('plys_shop_id');
    if (!shopId) return;

    this.createSession(shopId).subscribe(() => this.claimSession(false));
  }

  private createSession(shopId: string): Observable<{ id: string }> {
    const frontendSessionId = localStorage.getItem(this.frontendSessionKey);
    return this.sessionsService.createSession(frontendSessionId, shopId).pipe(
      first(),
      tap(({ id }) => {
        this.sessionId = id;
        localStorage.setItem(this.serverSessionKey, id);
      })
    );
  }

  private clearSession(): void {
    this.sessionId = null;
    localStorage.removeItem(this.serverSessionKey);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
